import React, { useState } from 'react';
import { Link, useLocation, NavLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { useHoverCapable } from '../hooks/useHoverCapable';
import { canAccessSubmissionsPage } from '../features/forms/utils/submissionPermissions';
import { Navbar, Nav, NavDropdown, Button } from '@/components/ui';
import {
  ADMIN_NAV_GROUPS,
  isAdminMenuVisible,
  isApprovalsMenuVisible,
  isGroupVisible,
  isProcurementVisible,
  visibleApprovalsItems,
  visibleGroupItems,
  type AdminNavGroup,
  type AdminNavItem,
} from './adminNavConfig';
import { useAwaitingApprovalsCount } from './useAwaitingApprovalsCount';
import NavCountBadge from './NavCountBadge';
import './NavBar.css';

interface AdminGroupFlyoutProps {
  group: AdminNavGroup;
  items: AdminNavItem[];
  isOpen: boolean;
  position: { top: number; left: number };
  onOpen: (anchor?: HTMLElement) => void;
  onClose: () => void;
  onNavigate: (path: string) => void;
}

/**
 * One expandable group inside the Admin menu.
 *
 * Hover opens the flyout on a mouse. On touch there is no `mouseleave`, so hovering alone left
 * the submenu stuck open; the group header is therefore a real button that toggles on tap. Its
 * click is stopped from bubbling because the parent `NavDropdown` menu closes on any click
 * inside it — correct for a leaf link, wrong for a header whose whole job is to open a submenu.
 */
const AdminGroupFlyout: React.FC<AdminGroupFlyoutProps> = ({
  group,
  items,
  isOpen,
  position,
  onOpen,
  onClose,
  onNavigate,
}) => {
  const hoverCapable = useHoverCapable();

  const hoverHandlers = hoverCapable
    ? {
        onMouseEnter: (e: React.MouseEvent<HTMLElement>) => onOpen(e.currentTarget),
        onMouseLeave: onClose,
      }
    : {};

  return (
    <div className="dropdown-item-hoverable">
      <NavDropdown.Item
        as="div"
        role="button"
        tabIndex={0}
        aria-expanded={isOpen}
        className="dropdown-item-with-submenu"
        style={{ cursor: 'default' }}
        {...hoverHandlers}
        onClick={(e) => {
          e.stopPropagation();
          if (isOpen) onClose();
          else onOpen(e.currentTarget as HTMLElement);
        }}
      >
        <span className="tw-flex tw-items-center tw-justify-between tw-w-full">
          {group.label}
          <i className="bi bi-chevron-right tw-ml-2"></i>
        </span>
        {isOpen && (
          <div
            className="submenu-dropdown"
            style={{
              top: `${position.top}px`,
              left: `${position.left}px`,
            }}
            {...(hoverCapable ? { onMouseEnter: () => onOpen(), onMouseLeave: onClose } : {})}
          >
            {items.map((item) => (
              <NavDropdown.Item
                key={item.to}
                as={NavLink}
                to={item.to}
                onClick={() => {
                  onNavigate(item.to);
                  onClose();
                }}
              >
                {item.label}
              </NavDropdown.Item>
            ))}
          </div>
        )}
      </NavDropdown.Item>
    </div>
  );
};

const NavBar: React.FC = () => {
  const location = useLocation();
  const { currentUser: user, logout, loading } = useAuth();
  const { hasPermission } = usePermissions();
  const [openAdminGroupId, setOpenAdminGroupId] = useState<string | null>(null);
  const [adminSubmenuPosition, setAdminSubmenuPosition] = useState({ top: 0, left: 0 });

  const handleNavClick = (path: string) => {
    if (location.pathname === path) {
      window.location.reload();
    }
  };

  const approvalsVisible = isApprovalsMenuVisible(hasPermission);
  const approvalsItems = visibleApprovalsItems(hasPermission);
  const procurementVisible = isProcurementVisible(hasPermission);
  const { byPath: awaitingByPath, total: awaitingTotal } = useAwaitingApprovalsCount(
    !loading && approvalsVisible,
    approvalsItems.map((item) => item.to),
    location.pathname
  );

  // Consistent environment detection with server
  // Priority: REACT_APP_ENVIRONMENT > NODE_ENV > default to 'development'
  const env = process.env.REACT_APP_ENVIRONMENT || process.env.NODE_ENV || 'development';
  const normalizedEnv = env.toLowerCase();

  // Validate environment (same as server)
  const validEnvironments = ['development', 'staging', 'testing', 'production'];
  const releaseStage = validEnvironments.includes(normalizedEnv) ? normalizedEnv : 'development';

  // Format environment string to capitalize first letter
  const formatEnv = (env: string) => {
    return env.charAt(0).toUpperCase() + env.slice(1).toLowerCase();
  };

  // "staging" is the underlying env value (Bugsnag, deploy scripts, and the server's own
  // validation all key off that exact string) but users know it as the training/DRM
  // environment, so the label shows that name instead of the raw release stage.
  const ENV_BADGE_LABELS: Record<string, string> = {
    staging: 'Training',
  };

  // Color the whole control bar per non-production stage so it's obvious at a glance, not
  // just readable on close inspection - staging/training is the one most likely to be
  // mistaken for production by someone clicking through quickly. Applied as an inline style
  // (rather than a `tw-bg-*` class) because it has to beat the Navbar component's own
  // `tw-bg-ink` utility: two same-specificity background utilities are decided by which one
  // is later in the compiled stylesheet, not by which comes later in the class list, so a
  // second class here would be an unreliable way to override it.
  const ENV_NAVBAR_BG: Record<string, string> = {
    development: '#2563eb', // tw-blue-600
    testing: '#166534', // tw-green-800
    staging: '#dc2626', // tw-red-600
  };
  const navbarStyle: React.CSSProperties | undefined =
    releaseStage !== 'production' ? { backgroundColor: ENV_NAVBAR_BG[releaseStage] } : undefined;

  const envBadge = releaseStage !== 'production' && (
    <span className="tw-ml-2 tw-rounded tw-border tw-border-white/40 tw-bg-black/15 tw-px-2 tw-py-0.5 tw-text-xs tw-font-bold tw-uppercase tw-tracking-wide tw-text-white">
      {ENV_BADGE_LABELS[releaseStage] ?? formatEnv(releaseStage)}
    </span>
  );

  // Don't render navigation items while loading
  if (loading) {
    return (
      <Navbar bg="dark" variant="dark" expand="lg" className="tw-w-full tw-px-3 tw-relative tw-z-[1030] tw-shrink-0" style={navbarStyle}>
        <Navbar.Brand className="tw-flex tw-items-center">
          <Link to="/home" className="tw-flex tw-items-center tw-no-underline">
            <img
              src={encodeURI(`${process.env.PUBLIC_URL || ''}/WRAP logo no border.png`)}
              alt="WRAP Logo"
              style={{
                height: '30px',
                marginRight: '10px'
              }}
            />
          </Link>
          {envBadge}
        </Navbar.Brand>
        <Nav className="navbar-nav tw-flex-row tw-items-center tw-ml-2">
          <Nav.Link
            as={NavLink}
            to="/home"
            onClick={() => handleNavClick('/home')}
          >
            Home
          </Nav.Link>
        </Nav>
      </Navbar>
    );
  }

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <Navbar bg="dark" variant="dark" expand="lg" className="tw-w-full tw-px-3 tw-relative tw-z-[1030] tw-shrink-0" style={navbarStyle}>
      <Navbar.Brand className="tw-flex tw-items-center">
        <Link to="/home" className="tw-flex tw-items-center tw-no-underline">
          <img
            src={encodeURI(`${process.env.PUBLIC_URL || ''}/WRAP logo no border.png`)}
            alt="WRAP Logo"
            style={{
              height: '30px',
              marginRight: '10px'
            }}
          />
        </Link>
        {envBadge}
      </Navbar.Brand>
      <Navbar.Toggle aria-controls="navbarNav" />
      <Navbar.Collapse id="navbarNav">
        <Nav className="tw-mr-auto">
          <Nav.Link
            as={NavLink}
            to="/home"
            onClick={() => handleNavClick('/home')}
          >
            Home
          </Nav.Link>
          {(hasPermission('quantified:task_create') || hasPermission('quantified:view') || hasPermission('quantified:sign_work_only') || hasPermission('routine:specific_create') || hasPermission('routine:view') || hasPermission('project:view') || hasPermission('project:task_edit') || hasPermission('project:task_create')) && (
            <NavDropdown 
              title="Task Management" 
              id="task-management-dropdown"
            >
              {(hasPermission('quantified:task_create') || hasPermission('quantified:view') || hasPermission('quantified:sign_work_only')) && (
                <NavDropdown.Item
                  as={NavLink}
                  to="/qp-tracking"
                  onClick={() => handleNavClick('/qp-tracking')}
                >
                  Quantified Work
                </NavDropdown.Item>
              )}
              {(hasPermission('routine:specific_create') || hasPermission('routine:view')) && (
                <NavDropdown.Item
                  as={NavLink}
                  to="/routine-work"
                  onClick={() => handleNavClick('/routine-work')}
                >
                  Routine Work
                </NavDropdown.Item>
              )}
              {(hasPermission('project:task_edit') || hasPermission('project:task_create') || hasPermission('project:view')) && (
                <NavDropdown.Item
                  as={NavLink}
                  to="/project-works"
                  onClick={() => handleNavClick('/project-works')}
                >
                  Project Works
                </NavDropdown.Item>
              )}
              <NavDropdown.Item
                as={NavLink}
                to="/all-tasks"
                onClick={() => handleNavClick('/all-tasks')}
              >
                All Tasks
              </NavDropdown.Item>
            </NavDropdown>
          )}
          {hasPermission('schedule:view') && (
            <Nav.Link
              as={NavLink}
              to="/schedule"
              onClick={() => handleNavClick('/schedule')}
            >
              Schedule
            </Nav.Link>
          )}

          {hasPermission('project:view') && (
            <Nav.Link
              as={NavLink}
              to="/projects"
              onClick={() => handleNavClick('/projects')}
            >
              Projects
            </Nav.Link>
          )}

          {canAccessSubmissionsPage(hasPermission) && (
            <Nav.Link
              as={NavLink}
              to="/approvals"
              onClick={() => handleNavClick('/approvals')}
            >
              Submissions
            </Nav.Link>
          )}
          {canAccessSubmissionsPage(hasPermission) && (
            <Nav.Link
              as={NavLink}
              to="/forms"
              onClick={() => handleNavClick('/forms')}
            >
              Forms
            </Nav.Link>
          )}
          {approvalsVisible && (
            <NavDropdown
              title={
                <span className="tw-inline-flex tw-items-center tw-gap-1.5">
                  Approvals
                  {awaitingTotal > 0 && (
                    <span className="tw-sr-only">{`, ${awaitingTotal} awaiting`}</span>
                  )}
                  <NavCountBadge count={awaitingTotal} />
                </span>
              }
              id="approvals-dropdown"
            >
              {approvalsItems.map((item) => (
                <NavDropdown.Item
                  key={item.to}
                  as={NavLink}
                  to={item.to}
                  onClick={() => handleNavClick(item.to)}
                >
                  <span className="tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-3">
                    {item.label}
                    {awaitingByPath[item.to] > 0 && (
                      <span className="tw-sr-only">{`, ${awaitingByPath[item.to]} awaiting`}</span>
                    )}
                    <NavCountBadge count={awaitingByPath[item.to] ?? 0} />
                  </span>
                </NavDropdown.Item>
              ))}
            </NavDropdown>
          )}
          {procurementVisible && (
            <Nav.Link
              as={NavLink}
              to="/purchase-orders"
              onClick={() => handleNavClick('/purchase-orders')}
            >
              Procurement
            </Nav.Link>
          )}
          {(hasPermission('submission:view') || hasPermission('pcr:create')) && (
            <Nav.Link
              as={NavLink}
              to="/pcr-submissions"
              onClick={() => handleNavClick('/pcr-submissions')}
            >
              PCR
            </Nav.Link>
          )}
          {hasPermission('workId:view') && (
            <Nav.Link
              as={NavLink}
              to="/work-ids"
              onClick={() => handleNavClick('/work-ids')}
            >
              Work IDs
            </Nav.Link>
          )}

          {(
            hasPermission('report:view') ||
            hasPermission('report:material_usage_create') ||
            hasPermission('report:labour_submitted_time_create') ||
            hasPermission('report:finance_monthend_create')
          ) && (
            <Nav.Link
              as={NavLink}
              to="/reports"
              onClick={() => handleNavClick('/reports')}
            >
              Reports
            </Nav.Link>
          )}

          {/* Admin: grouped flyouts. Visible if the user can open at least one page. */}
          {isAdminMenuVisible(hasPermission) && (
            <NavDropdown
              title="Admin"
              id="admin-dropdown"
            >
              {ADMIN_NAV_GROUPS.filter((group) => isGroupVisible(group, hasPermission)).map(
                (group) => (
                  <AdminGroupFlyout
                    key={group.id}
                    group={group}
                    items={visibleGroupItems(group, hasPermission)}
                    isOpen={openAdminGroupId === group.id}
                    position={adminSubmenuPosition}
                    onOpen={(anchor) => {
                      if (anchor) {
                        const rect = anchor.getBoundingClientRect();
                        setAdminSubmenuPosition({ top: rect.top, left: rect.right + 4 });
                      }
                      setOpenAdminGroupId(group.id);
                    }}
                    onClose={() => setOpenAdminGroupId((current) => (current === group.id ? null : current))}
                    onNavigate={handleNavClick}
                  />
                )
              )}
            </NavDropdown>
          )}
        </Nav>
        {user && (
          <div className="navbar-text tw-text-white tw-flex tw-shrink-0 tw-items-center tw-ml-auto">
            <span className="tw-mr-3 tw-hidden tw-whitespace-nowrap tw-text-sm xl:tw-inline">
              Hello, {`${user.name} ${user.lastName}`}
            </span>
            <Button
              variant="outline-light"
              size="sm"
              onClick={handleLogout}
            >
              Logout
            </Button>
          </div>
        )}
      </Navbar.Collapse>
    </Navbar>
  );
};

// Add a class to the main content area in your main layout or App component
// Example: <div className="main-content pt-5">...</div>
// Ensure you have the appropriate CSS for .pt-5 to add padding-top

export default NavBar; 